import api from "@/api";
import filesApi from "@/api/files";

import saveAs from "file-saver";

function hasProp(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

function initialState() {
  return {
    localFile: null,
    maxFileSize: 16000000,
    uploadPercent: 0,
    uploadedFile: {},
    uploadedFiles: [],
    duplicateFile: {},
    duplicateFileResource: {},
    duplicateMatch: null,
    fileUploadErrors: [],
    fileSrc: null,
    files: [],
    imageArray: [],
    filters: {
      searchQuery: "",
      size: 10,
      blocked: 0,
    },
    sortBy: {
      field: "file_id",
      direction: "asc",
    },
    pagination: {
      total: 10,
      per_page: 10,
      current_page: 1,
      last_page: 1,
      from: 1,
      to: 10,
    },
  };
}

export default {
  namespaced: true,

  state: initialState,

  getters: {
    uploadComplete(state) {
      return state.uploadedFile.id && state.uploadPercent >= 100;
    },

    filteredList(state) {
      return state.files.map((file) => ({
        id: file.self,
        name: file.name,
        documentId: file.id,
        type: "file",
      }));
    },
  },

  mutations: {
    SET_FILTER_SIZE(state, size) {
      state.filters.size = size;
    },
    SET_RESULT_COUNT(state, number) {
      state.filters.size = number;
      state.pagination.per_page = number;
      state.pagination.total = number;
      state.pagination.to = number;
    },
    SET_FILTER_TOTAL(state, total) {
      state.pagination.total = total;
      state.pagination.last_page = Math.ceil(total / state.pagination.per_page);
    },
    SET_CURRENT_PAGE(state, page) {
      state.pagination.current_page = page;
    },
    ADD_IMAGE_DATA(state, payload) {
      state.imageArray.push(payload);
    },
    setSearchQuery(state, val) {
      state.filters.searchQuery = val;
    },
    setLocalFile(state, file) {
      state.localFile = file;
    },

    setUploadPercent(state, uploadPercent) {
      state.uploadPercent = uploadPercent;
    },

    setUploadedFile(state, file) {
      state.uploadedFile = Object.assign({}, file);
    },

    setDuplicateFile(state, file) {
      state.duplicateFile = Object.assign({}, file);
    },

    setDuplicateFileResource(state, resource) {
      state.duplicateFileResource = Object.assign({}, resource);
    },

    setDuplicateFileMatch(state, match) {
      state.duplicateMatch = match;
    },

    reset(state) {
      const s = initialState();

      // acquire initial state
      Object.keys(s).forEach((key) => {
        if (key !== "files") {
          state[key] = s[key];
        }
      });
    },

    setFileUploadErrors(state, uploadErrors) {
      state.fileUploadErrors = uploadErrors;
    },

    addFileUploadError(state, fileUploadError) {
      state.fileUploadErrors.push(fileUploadError);
    },

    setFileSrc(state, fileSrc) {
      state.fileSrc = fileSrc;
    },

    SET_FILES(state, files) {
      state.files = files;
    },
    ADD_UPLOADED_FILE(state, file) {
      state.files.unshift(file); // move to start
    },
  },

  actions: {
    async list({ state, commit }, options) {
      let resp;
      const localOptions = { ...options };
      const page = localOptions.page ? localOptions.page : 1;

      const size = state.pagination.per_page;
      let from = size * page - size;

      if (!from) {
        from = 0;
      }

      const filter = {
        size,
        from,
        sort: [
          {
            created_time: {
              order: "desc",
            },
          },
        ],
        query: {
          bool: {
            must_not: [
              {
                terms: {
                  resource_types: ["policy", "forms"],
                },
              },
            ],
          },
        },
      };

      if (state.filters.size) {
        filter.size = state.filters.size;
      }

      if (state.filters.searchQuery) {
        filter.query.bool.must = [
          {
            match_phrase_prefix: {
              name: {
                query: state.filters.searchQuery,
                slop: 10,
              },
            },
          },
        ];
      }

      try {
        resp = await api().put("/files/flat", { filter });
        commit("SET_FILES", resp.data.data.file);
        commit("SET_FILTER_TOTAL", resp.data.meta.filter_total);
        commit("SET_CURRENT_PAGE", page);
        return Promise.resolve(resp.data.data.file);
      } catch (err) {
        commit("SET_FILES", []);
        return Promise.reject({ ...err });
      }
    },

    async listAllForSelect(context, filterTerm) {
      let resp;
      const size = 5;
      const from = 0;

      // content first
      const filter = {
        size,
        from,
        sort: [
          {
            created_time: {
              order: "desc",
            },
          },
        ],
        query: {
          bool: {
            must_not: [
              {
                terms: {
                  resource_types: ["policy", "forms"],
                },
              },
            ],
          },
        },
      };

      if (filterTerm) {
        filter.query.bool.must = [
          {
            match_phrase_prefix: {
              name: {
                query: filterTerm,
                slop: 10,
              },
            },
          },
        ];
      }

      try {
        resp = await api().put("/files/flat", { filter });
        return Promise.resolve(resp.data.data.file);
      } catch (err) {
        return Promise.resolve([]);
      }
    },
    upload({ commit, state }, { fileData, resource }) {
      commit("setUploadedFile", {});
      commit("setDuplicateFile", {});
      commit("setDuplicateFileMatch", null);
      commit("setDuplicateFileResource", {});
      commit("setFileUploadErrors", []);

      fileData.secure = 0;

      const config = {
        timeout: false,
        onUploadProgress(progressEvent) {
          commit(
            "setUploadPercent",
            Math.round((progressEvent.loaded * 100) / progressEvent.total)
          );
        },
      };

      return new Promise((resolve, reject) => {
        filesApi
          .create(fileData, config)
          .then((response) => {
            commit("setUploadedFile", response.file);
            resolve(response);
            commit("ADD_UPLOADED_FILE", response.file);
          })
          .catch((error) => {
            if (error.status === 413) {
              commit("setUploadedFile", {});
              commit("setDuplicateFile", {});
              commit("setDuplicateFileMatch", null);
              commit("setDuplicateFileResource", {});
              commit("setLocalFile", null);

              commit(
                "addFileUploadError",
                `The file uploaded was too large. Maximum file size is ${error.data.meta.max_file_size}`
              );
              reject(error);
            } else {
              const hasUploadedFile = hasProp(error.data.data, "file");
              let hasDuplicateFileResource;
              if (!resource.singular || resource.singular === "file") {
                hasDuplicateFileResource =
                  hasProp(error.data.meta, "duplicate_file_policy") ||
                  hasProp(error.data.meta, "duplicate_file_form");
              } else {
                hasDuplicateFileResource = hasProp(
                  error.data.meta,
                  `duplicate_file_${resource.singular}`
                );
              }

              const hasDuplicateFile = hasProp(
                error.data.meta,
                "duplicate_file"
              );
              if (hasDuplicateFileResource) {
                commit("setLocalFile", null);
                let errorMessage;
                if (!resource.singular || resource.singular === "file") {
                  errorMessage = error.data.meta.duplicate_file_policy
                    ? error.data.meta.duplicate_file_policy
                    : error.data.meta.duplicate_file_form;
                } else {
                  errorMessage =
                    error.data.meta[`duplicate_file_${resource.singular}`];
                }
                commit("setDuplicateFileResource", errorMessage);
                reject(error);
              }
              if (hasDuplicateFile) {
                // alert('b');
                commit(
                  "setDuplicateFileMatch",
                  error.data.meta.duplicate_match
                );
                commit("setDuplicateFile", error.data.meta.duplicate_file);
              }
              if (hasUploadedFile) {
                // alert('c');
                commit("setUploadedFile", error.data.data.file[0]);
                resolve(error.data.data.file[0]);
              }
              // console.log({...error})
              // alert('d');
              // When duplicate but no duplicate resource and the file was not returned...
              if (
                hasDuplicateFile &&
                !hasDuplicateFileResource &&
                !hasUploadedFile
              ) {
                // set the uploaded file with the duplicate file instead
                commit("setUploadedFile", {
                  ...error.data.meta.duplicate_file,
                  isDuplicate: true,
                });
                resolve(error.data.meta.duplicate_file);
              }

              // alert('e');

              // down here something really bad happened
              if (!state.duplicateFile) {
                commit(
                  "addFileUploadError",
                  "The file could not be uploaded.  Please try again or use a new file"
                );
                console.log({ ...error });
                commit("setUploadedFile", {});
                commit("setDuplicateFile", {});
                commit("setDuplicateFileResource", {});
                commit("setLocalFile", null);
                reject(error);
              }
            }
          });
      });
    },

    download({ commit, state }, fileData) {
      return new Promise((resolve, reject) => {
        filesApi
          .readLink(fileData.link)
          .then((response) => {
            const blob = new Blob([response.data], {
              type: response.headers["content-type"],
            });
            saveAs(blob, fileData.name);
            resolve();
          })
          .catch((error) => {
            reject("file download error: ", error);
          });
      });
    },

    read({ commit }, fileId) {
      commit("setUploadedFile", {});

      return new Promise((resolve, reject) => {
        filesApi
          .read(fileId)
          .then((file) => {
            resolve(file);
            commit("setUploadedFile", file);
            commit("setLocalFile", { name: file.file_name });
            commit("setUploadPercent", 100);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    readLink({ commit, state }, fileLink) {
      filesApi
        .readLink(fileLink)
        .then((response) => {
          const blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });
          commit("setFileSrc", blob);
        })
        .catch((error) => {
          console.error("file download error: ", error);
        });
    },

    async delete({ commit, state }, fileId) {
      let resp;

      try {
        resp = await api().delete(`/file/${fileId}`);
        return Promise.resolve(resp);
      } catch (err) {
        return Promise.reject({ ...err });
      }
    },

    /**
     * Methods to try and help to stop multiple calls for AJAX
     */
    async getImageFromStore({ state }, imgId) {
      const image = state.imageArray.filter((img) => img.id === imgId);

      if (image.length > 0) {
        return Promise.resolve(image);
      }
      return Promise.reject(new Error("no image found"));
    },

    async addImageToStore({ commit }, image) {
      const imageObj = {
        id: image.id,
        src: image.base64data,
      };

      commit("ADD_IMAGE_DATA", imageObj);

      return Promise.resolve(imageObj);
    },
  },
};
